"use client"

import { Fragment, useRef, useState } from "react"
import { ThumbsUp, ThumbsDown } from "lucide-react"
import type { Message, ToolCall } from "./types"
import { FeedbackDialog } from "./FeedbackDialog"
import { HighlightFeedbackPopover } from "./HighlightFeedbackPopover"
import { getToolRenderer, isWidgetTool } from "@/hooks/useToolRenderer"
import { MarkdownRenderer } from "./MarkdownRenderer"
import { useChatMessagePresentation } from "./ChatMessagePresentation"

interface ChatMessageProps {
  message: Message
  sessionId: string
  /**
   * Callback to submit feedback for this message.
   *
   * @param feedbackType - "positive" or "negative"
   * @param comment - Free-text comment from the dialog.
   * @param scope - What the feedback targets: whole "message", a "highlight", or a "tool".
   * @param messageOverride - When provided, use this string as the feedback message
   *   instead of the full message content (used for highlight/tool feedback).
   */
  onFeedbackSubmit: (
    feedbackType: "positive" | "negative",
    comment: string,
    scope: FeedbackScope,
    messageOverride?: string
  ) => Promise<void>
}

/** What a piece of feedback targets within an assistant message. */
export type FeedbackScope = "message" | "highlight" | "tool"

/**
 * Thumbs up/down feedback buttons for a tool call. Shown on hover of the
 * parent element (which must have the `group/tool` class).
 *
 * @param onFeedback - Called with the chosen feedback type when a button is clicked.
 * @param className - Optional extra classes for the wrapping container (e.g. positioning).
 */
function ToolFeedbackButtons({
  onFeedback,
  className = "",
}: {
  onFeedback: (feedbackType: "positive" | "negative") => void
  className?: string
}): React.JSX.Element {
  return (
    <div
      className={`flex items-center gap-0.5 opacity-0 group-hover/tool:opacity-100 transition-opacity ${className}`}
    >
      <button
        onClick={() => onFeedback("positive")}
        className="p-0.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
        aria-label="Positive feedback on tool result"
      >
        <ThumbsUp size={12} />
      </button>
      <button
        onClick={() => onFeedback("negative")}
        className="p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
        aria-label="Negative feedback on tool result"
      >
        <ThumbsDown size={12} />
      </button>
    </div>
  )
}

export function ChatMessage({
  message,
  onFeedbackSubmit,
}: ChatMessageProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedFeedbackType, setSelectedFeedbackType] = useState<"positive" | "negative">(
    "positive"
  )
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  /** Override for the message field in the feedback payload (highlighted text or tool summary). */
  const [messageOverride, setMessageOverride] = useState<string | null>(null)
  /** What the pending feedback targets (whole message, a highlight, or a tool). */
  const [feedbackScope, setFeedbackScope] = useState<FeedbackScope>("message")
  const presentation = useChatMessagePresentation()
  /** Ref to the assistant message content container for selection detection. */
  const assistantContentRef = useRef<HTMLDivElement>(null)

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  /** Opens the feedback dialog for whole-message feedback. */
  const handleFeedbackClick = (type: "positive" | "negative") => {
    setSelectedFeedbackType(type)
    setMessageOverride(null)
    setFeedbackScope("message")
    setIsDialogOpen(true)
  }

  /**
   * Called from the HighlightFeedbackPopover when the user clicks a
   * feedback button on selected text. Opens the dialog with the
   * highlighted text as the message override.
   */
  const handleHighlightFeedback = (
    feedbackType: "positive" | "negative",
    text: string
  ) => {
    setSelectedFeedbackType(feedbackType)
    setMessageOverride(text)
    setFeedbackScope("highlight")
    setIsDialogOpen(true)
  }

  /**
   * Called when the user clicks a feedback button on a tool call result.
   * Opens the dialog with a formatted tool summary as the message override.
   *
   * @param feedbackType - "positive" or "negative"
   * @param toolCall - The tool call being rated.
   */
  const handleToolFeedback = (
    feedbackType: "positive" | "negative",
    toolCall: ToolCall
  ) => {
    const toolSummary = `[Tool: ${toolCall.name}]\nInput: ${toolCall.input}\nResult: ${toolCall.result ?? "N/A"}`
    setSelectedFeedbackType(feedbackType)
    setMessageOverride(toolSummary)
    setFeedbackScope("tool")
    setIsDialogOpen(true)
  }

  /**
   * Unified submit handler for the feedback dialog. Passes the scope and the
   * messageOverride (highlighted text or tool summary) when the user targeted
   * a specific part of the message.
   */
  const handleFeedbackSubmit = async (comment: string) => {
    await onFeedbackSubmit(
      selectedFeedbackType,
      comment,
      feedbackScope,
      messageOverride ?? undefined
    )
    // Only lock the whole-message thumbs buttons after whole-message feedback.
    if (feedbackScope === "message") {
      setFeedbackSubmitted(true)
    }
    setMessageOverride(null)
    setFeedbackScope("message")
  }

  const renderAssistantContent = () => {
    const footer = presentation.renderAssistantFooter(message)
    const renderAssistantText = (text: string, key?: React.Key) => {
      const content = presentation.formatAssistantText(text, message)
      if (!content.trim()) return null
      const rendered = presentation.renderAssistantText ? (
        presentation.renderAssistantText(content, message)
      ) : (
        <MarkdownRenderer content={content} />
      )
      return key === undefined ? rendered : <Fragment key={key}>{rendered}</Fragment>
    }

    // If segments exist, render them in order (interleaved text + tools)
    if (message.segments && message.segments.length > 0) {
      return (
        <>
          {message.segments.map((seg, i) => {
            if (seg.type === "text") {
              return renderAssistantText(seg.content, i)
            }
            const render = getToolRenderer(seg.toolCall.name)
            if (!render) return null

            const isWidget = isWidgetTool(seg.toolCall.name)
            const isComplete = seg.toolCall.status === "complete"

            if (isWidget) {
              // Widget tools: feedback buttons below the rendered widget
              return (
                <div key={seg.toolCall.toolUseId} className="my-1 group/tool">
                  {render({
                    name: seg.toolCall.name,
                    args: seg.toolCall.input,
                    status: seg.toolCall.status,
                    result: seg.toolCall.result,
                  })}
                  {isComplete && (
                    <ToolFeedbackButtons
                      onFeedback={type => handleToolFeedback(type, seg.toolCall)}
                    />
                  )}
                </div>
              )
            }

            // Default tool display: feedback buttons inline at the end
            return (
              <div key={seg.toolCall.toolUseId} className="group/tool relative">
                {render({
                  name: seg.toolCall.name,
                  args: seg.toolCall.input,
                  status: seg.toolCall.status,
                  result: seg.toolCall.result,
                })}
                {isComplete && (
                  <ToolFeedbackButtons
                    onFeedback={type => handleToolFeedback(type, seg.toolCall)}
                    className="absolute top-1/2 -translate-y-1/2 left-full ml-1"
                  />
                )}
              </div>
            )
          })}
          {footer}
        </>
      )
    }

    // Fallback: just render content as markdown
    return (
      <>
        {renderAssistantText(message.content)}
        {footer}
      </>
    )
  }

  return (
    <div className={`flex flex-col ${message.role === "user" ? "items-end" : "items-start"}`}>
      <div
        ref={message.role === "assistant" ? assistantContentRef : undefined}
        className={`max-w-[90%] wrap-break-word ${
          message.role === "user"
            ? "p-3 rounded-lg bg-gray-800 text-white rounded-br-none whitespace-pre-wrap"
            : "text-foreground"
        }`}
      >
        {message.role === "assistant" ? renderAssistantContent() : message.content}

        {/* Highlight feedback popover — shown when user selects text in assistant messages */}
        {message.role === "assistant" && !message.isStreaming && (
          <HighlightFeedbackPopover
            containerRef={assistantContentRef}
            disabled={message.isStreaming}
            onHighlightFeedback={handleHighlightFeedback}
          />
        )}
      </div>

      {/* Timestamp and Feedback buttons for assistant messages */}
      <div className="flex items-center gap-2 mt-1 px-1">
        <div className="text-xs text-gray-400">{formatTime(message.timestamp)}</div>

        {/* Show feedback buttons only for assistant messages with content */}
        {message.role === "assistant" && message.content && (
          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={() => handleFeedbackClick("positive")}
              disabled={feedbackSubmitted}
              className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Positive feedback"
              title="Good response"
            >
              <ThumbsUp size={14} />
            </button>
            <button
              onClick={() => handleFeedbackClick("negative")}
              disabled={feedbackSubmitted}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Negative feedback"
              title="Bad response"
            >
              <ThumbsDown size={14} />
            </button>
            {feedbackSubmitted && (
              <span className="text-xs text-gray-400 ml-1">Thanks for your feedback!</span>
            )}
          </div>
        )}
      </div>

      {/* Feedback Dialog */}
      <FeedbackDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false)
          setMessageOverride(null)
          setFeedbackScope("message")
        }}
        onSubmit={handleFeedbackSubmit}
        feedbackType={selectedFeedbackType}
      />
    </div>
  )
}
